// Die gemeinsamen Stile der Abschnitte des Kontaktdetails (Party 360, erster
// Schnitt). Aus kontakt.tsx herausgeloest; die Werte sind unveraendert.

import { makeStyles, tokens } from '@fluentui/react-components';

import { ABSCHNITT_ABSTAND, ABSCHNITT_SCHRIFT } from '../../shell/masse.ts';

export const useKontaktStyles = makeStyles({
  kopf: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '0.75rem',
  },
  gruppe: {
    marginTop: '1rem',
  },
  liste: {
    listStyle: 'none',
    margin: '0.25rem 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  wert: {
    display: 'block',
    overflowWrap: 'anywhere',
  },
  merkmale: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
  },
  hinweis: {
    display: 'block',
    marginTop: '0.75rem',
    color: tokens.colorNeutralForeground2,
  },
  // Verknüpfungszeichen hinter dem Feldnamen einer übernommenen Angabe, 4 px Abstand.
  // Kein nowrap: mit vergrößerten Textabständen (WCAG 1.4.12) muss der Name in
  // seiner Spalte umbrechen können; am letzten Stück hält ihn ein Wortverbinder.
  uebernommenZeichen: {
    '& > svg': { marginInlineStart: tokens.spacingHorizontalXS, verticalAlign: '-0.2em' },
  },
  // "über <Organisation>" unter übernommenen Werten (P7 A): Metazeile, ein langer Name bricht um.
  ueberOrganisation: {
    display: 'block',
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'anywhere',
  },
  notiz: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  // Die Beschriftung des Notizfelds bleibt sichtbar (WCAG 3.3.2), aber klein und
  // still: v4 hat ueber dem Kasten nur die Abschnittsueberschrift.
  notizBeschriftung: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
  },
  // Die Auswahl der Stichworte. Volle Breite, damit bei 320 CSS-Pixeln weder
  // der Begriff noch seine Definition abgeschnitten wird (NFR-ACC-001).
  wahl: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '0.5rem',
    maxWidth: '100%',
  },
  // Die Auswahl "Neue Rolle" im Listeneintrag: ohne diese Grenze haelt das
  // Dropdown seine Mindestbreite und laeuft bei 320 CSS-Pixeln und 200 Prozent
  // Textzoom ueber den Rand (gemessen, Probe C5; ADR-0057).
  auswahlfeld: {
    minWidth: 0,
    maxWidth: '100%',
  },
  eintrag: {
    borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
    paddingLeft: '0.75rem',
  },
  text: {
    display: 'block',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  // Die Wiedervorlage an einer Notiz (ST-ACT-001, zweiter Schnitt). Sie steht
  // UNTER der Notiz und kleiner: der Verlauf ist der Gegenstand, die
  // Wiedervorlage seine Randnotiz (5.2.9).
  vormerkung: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.25rem',
  },
  vormerkformular: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '0.5rem',
    marginTop: '0.25rem',
  },
  // Eine Flaeche auf dem Papier: Kopf, Hinweis und jedes Panel der
  // Registerleiste sind je eine Karte (Slice 4b, Entwurf v4).
  flaeche: {
    padding: '1.25rem',
    // v4: Linie UND Schatten.
    boxShadow: tokens.shadow4,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
    // Der erste Abschnitt einer Karte steht ohne oberen Abstand: Innenabstand
    // und `gruppe` (marginTop) addierten sich zu 40 bis 58 px (Review #380).
    '& > section:first-child, & > div:first-child > section:first-child': { marginTop: 0 },
    // Auch der Block-h2 des ersten Abschnitts (Verlauf) ohne Browser-Aussenabstand.
    '& > section:first-child > h2, & > section:first-child > [data-abschnittkopf] > h2': { marginTop: 0 },
    // Abschnittsueberschriften 15 px / 600 (v4 fs-h2). Die Akkordeonkoepfe sind
    // keine section > h2 und bleiben, wie sie sind.
    '& section > h2, & section > [data-abschnittkopf] > h2': {
      display: 'block',
      fontSize: ABSCHNITT_SCHRIFT,
      lineHeight: tokens.lineHeightBase300,
      fontWeight: tokens.fontWeightSemibold,
    },
    // Eine Linie (v4 line) trennt jeden Abschnitt vom vorigen.
    // Auch nach einem Behaelter (die Ansprechpartner ueber dem Verlauf, Slice 6b).
    '& section + section, & section + div, & > div + section': {
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
      paddingTop: ABSCHNITT_ABSTAND,
    },
  },
  // Ueberschrift und Pflegeweg in einer Zeile; der Weg steht am Ende (v4 h2 .end).
  abschnittKopf: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '0.25rem 0.75rem',
    // Die Ueberschrift nimmt den Platz, die Wege stehen gemeinsam rechts.
    '& > h2': { margin: 0, marginInlineEnd: 'auto' },
  },
  // Der Pflegeweg am Ende der Ueberschriftenzeile: klein, aber ein Ziel von
  // mindestens 24 px Hoehe (WCAG 2.5.8).
  pflegeweg: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: '1.5rem',
    minHeight: '1.5rem',
    display: 'inline-block',
  },
  grossAnfang: { display: 'inline-block', '::first-letter': { textTransform: 'uppercase' } },
  nurVorgelesen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'pre',
  },
  // Eine Ansprechpartner-Zeile (v4 secAnsprech): Name und Angaben fliessen, das
  // Menue "…" steht rechts in derselben Zeile; 28 px Ziel (WCAG 2.5.8).
  ansprechZeile: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    minHeight: '2rem',
  },
  ansprechText: {
    flex: '1 1 auto',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  ansprechMenue: {
    flexShrink: 0,
    minWidth: '1.75rem',
    minHeight: '1.75rem',
  },
  // "Ansprechpartner anlegen" und "…" bleiben beieinander; die Gruppe rutscht als
  // Ganzes unter die h2, wenn die Zeile zu schmal ist (DESIGN-P2 #530).
  kopfGruppe: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    flexWrap: 'nowrap',
    marginInlineStart: 'auto',
  },

  // "+ Rolle", "+ Beziehung", "Bestehenden Kontakt zuordnen" (v4 "+ Stichwort"):
  // still, in Markenfarbe, damit er sich vom Fliesstext abhebt, 28 px Ziel.
  abschnittPlus: {
    minHeight: '1.75rem',
    color: tokens.colorBrandForegroundLink,
    ':hover': { color: tokens.colorBrandForegroundLinkHover },
    ':hover:active': { color: tokens.colorBrandForegroundLinkPressed },
  },
  // Die Zahl neben "Ansprechpartner" (v4): still, nicht fett.
  kopfZahl: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    marginInlineStart: '0.375rem',
  },
  // "Weitere laden" und sein Ergebnis in einer Zeile; schmal bricht der Satz um.
  weitereZeile: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.25rem 0.75rem',
    marginTop: '0.5rem',
  },
  // Kleine Knoepfe am Abschnittskopf: 28 px wie die uebrigen kleinen Knoepfe (DESIGN-P3 #518).
  kopfKnopf: {
    minHeight: '1.75rem',
  },
  // Die vergebenen Stichworte als Marken in EINER Reihe (v4 tag, M-11): sie
  // brechen um wie Woerter, jede Marke ist ein Listeneintrag.
  stichwortReihe: {
    listStyle: 'none',
    margin: '0.25rem 0 0',
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.375rem',
  },
  // Ein vergebenes Stichwort: das Wort als Marke auf der vertieften Flaeche,
  // das Kreuz darin ist ein eigenes Ziel von 24 px (WCAG 2.5.8).
  stichwortZeile: {
    display: 'inline-flex',
    minWidth: 0,
  },
  stichwortMarke: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.125rem',
    padding: '0 0.125rem 0 0.5rem',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    minHeight: '1.5rem',
  },
  // Traegt eine Marke einen Hinweis (ersetzt, gesperrt), zeigt ein gestrichelter
  // Rand, dass der Tooltip mehr sagt; vorgelesen steht er ohnehin in der Marke.
  stichwortMarkeHinweis: {
    outline: `1px dashed ${tokens.colorNeutralStrokeAccessible}`,
    outlineOffset: '-1px',
  },
  stichwortWeg: {
    minWidth: '1.5rem',
    width: '1.5rem',
    height: '1.5rem',
    padding: 0,
    color: tokens.colorNeutralForeground3,
  },
  // Die Feldliste der Kontaktdaten und Anschriften (v4 field): Name links, Wert
  // rechts. Der Name bekommt bis 7.5rem, damit "Geschaeftsanschrift" mit dem
  // Zeichen der Uebernahme passt. Die Liste selbst ist der Behaelter: bleiben
  // dem Wert keine 10rem (Liste unter 18.25rem, etwa 247 px bei 768 und 1024),
  // steht der Name ueber dem Wert -- sonst bricht "99999 Musterhausen" um
  // (Probe A97).
  feldliste: {
    margin: '0.5rem 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    containerType: 'inline-size',
    containerName: 'feldliste',
  },
  feld: {
    display: 'grid',
    gridTemplateColumns: 'minmax(5.5rem, 7.5rem) minmax(0, 1fr)',
    columnGap: '0.75rem',
    alignItems: 'baseline',
    '@container feldliste (max-width: 18.25rem)': { gridTemplateColumns: 'minmax(0, 1fr)', rowGap: '0.125rem' },
  },
  // Bricht ein Name in seiner Spalte um (etwa mit vergrößerten Textabständen),
  // dann an der Silbe mit Trennstrich (lang="de"), erst zuletzt irgendwo.
  feldName: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground3,
    hyphens: 'auto',
    overflowWrap: 'anywhere',
  },
  feldWert: {
    margin: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  // Kopieren hinter dem Wert: 24 px Ziel (WCAG 2.5.8), etwas Luft zum Wert, auf der Textzeile.
  kopieren: { minWidth: '24px', minHeight: '24px', marginInlineStart: tokens.spacingHorizontalXS, verticalAlign: 'middle' },
  // Zwei Spalten, sobald das PANEL 44 rem breit ist (Behaelterabfrage, nicht das
  // Fenster): bei 1024 px Fenster bleiben neben der Navigation ~46 rem, und die
  // Hauptaktion "Notiz festhalten" muss dort im ersten Bildschirm stehen
  // (DESIGN-P1 #502, Probe B42c). Links die Datenkarte (v4 300-380 px), rechts
  // der Verlauf mit mindestens 26 rem. Der Behaelter ist das Panel der
  // Registerleiste; darin liegt nichts fixed Positioniertes ausser Fluents
  // Portalen, die ausserhalb haengen. Die Werte in rem folgen dem Textzoom, die
  // Abfrage auch -- bei 200 % bleibt es einspaltig, statt ueberzulaufen.
  spalten: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '1rem',
    alignItems: 'start',
    '@container kontaktpanel (min-width: 44rem)': {
      gridTemplateColumns: 'minmax(17.5rem, 23.75rem) minmax(26rem, 1fr)',
    },
  },
  // Im Register "Verlauf" steht der Verlauf allein, in jeder Breite.
  // Die rechte Spalte des Ueberblicks bleibt beim Scrollen stehen, sonst laege
  // neben langen Daten eine Leerflaeche (M-12, Michael D-08). Nur breit UND hoch
  // genug: ragte sie ueber das Fenster, waere ihr Ende nicht mehr erreichbar.
  // Nebenknopf in Markenfarbe (Muster B2), nicht grauer Kleintext.
  ganzerVerlauf: { color: tokens.colorBrandForeground1, ':hover': { color: tokens.colorBrandForeground2Hover } },
  spalteHaftend: {
    '@container kontaktpanel (min-width: 64rem)': {
      '@media (min-height: 56rem)': {
        position: 'sticky',
        // Bezug ist der Hauptbereich (M-20): top 0 haftet mit dessen Innenabstand, also
        // demselben Abstand zum Kopf wie ungescrollt; kein Versatz zur linken Spalte.
        top: 0,
        alignSelf: 'start',
      },
    },
  },
  spalteEinzeln: {
    '@container kontaktpanel (min-width: 44rem)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  // Unter 20 rem darf die Leiste in sich scrollen, nie die Seite (WCAG 1.4.10).
  leiste: {
    overflowX: 'auto',
    maxWidth: '100%',
  },
  tabs: {
    display: 'flex',
    gap: '0.25rem',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Das gewaehlte Register: Unterstrich (3 px, Markenfarbe) UND staerkere
  // Schrift, nicht nur Farbe (WCAG 1.4.1).
  tab: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    marginBottom: '-1px',
    padding: '0.5rem 0.75rem',
    minHeight: '2.5rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    ':hover': { color: tokens.colorNeutralForeground1, backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
  tabGewaehlt: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    borderBottomColor: tokens.colorBrandStroke1,
  },
  panel: {
    marginTop: '0.5rem',
    containerType: 'inline-size',
    containerName: 'kontaktpanel',
  },
  seite: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  // Die leere Statusregion der Seite liegt ausserhalb des Flusses: als Flex-Kind
  // kostete sie 16 px Abstand (v4 U2). Mit Inhalt steht sie wie jedes Kind.
  statusLeer: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  // --- Verlauf als Zeitleiste (Slice 5a) -----------------------------------
  // Die Fussleiste der Eingabe und die Filterleiste richten sich nach der
  // Breite des Behaelters, nicht nach dem Register: flex-wrap statt fester
  // Spalten, damit dieselbe Komponente in der Spalte und in voller Breite passt.
  // Der Behaelter der Eingabe: Fussleiste und Hinweis richten sich nach SEINER
  // Breite, nicht nach dem Register (derselbe Verlauf steht in der halben und in
  // voller Breite).
  eingabeBehaelter: {
    containerType: 'inline-size',
    containerName: 'eingabe',
  },
  // EIN Kasten um Textfeld und Fussleiste (v4 U9). Seine Grenze ist die Feldgrenze
  // (colorNeutralStrokeAccessible, >= 3:1, WCAG 1.4.11); im Fokus 2 px Marke. Das
  // Textfeld darin hat keinen eigenen Rand. 8 px Innenabstand: bei 12 px kippte der
  // erste Bildschirm (B60) auf 579 px.
  eingabeKasten: {
    border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '0.5rem',
    backgroundColor: tokens.colorNeutralBackground1,
    ':focus-within': {
      border: `2px solid ${tokens.colorBrandStroke1}`,
      padding: 'calc(0.5rem - 1px)',
    },
    '& .fui-Textarea': {
      border: 'none',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      width: '100%',
    },
    '& .fui-Textarea::after': { display: 'none' },
    // Das Feld waechst mit dem Text bis etwa sechs Zeilen, dann scrollt es in sich
    // (DESIGN-P3 #569). Ohne field-sizing bleibt es bei zwei Zeilen und dem Ziehgriff.
    '& .fui-Textarea__textarea': {
      fieldSizing: 'content',
      minHeight: `calc(2 * ${tokens.lineHeightBase300} + 0.75rem)`,
      maxHeight: `calc(6 * ${tokens.lineHeightBase300} + 0.75rem)`,
    },
  },
  // Ein abgewiesener Eintrag: der Kasten traegt die Fehlerfarbe, 2 px (WCAG 1.4.1:
  // dazu steht der Satz unter dem Kasten).
  eingabeKastenFehler: {
    border: `2px solid ${tokens.colorPaletteRedBorder2}`,
    padding: 'calc(0.5rem - 1px)',
    ':focus-within': {
      border: `2px solid ${tokens.colorPaletteRedBorder2}`,
    },
  },
  // 8 px zwischen Feld und Fussleiste, wie der Innenabstand des Kastens: ohne Abstand stiess der Knopf an den Ziehgriff des Feldes.
  eingabeFuss: {
    marginTop: '0.5rem',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '0.5rem 1rem',
  },
  eingabeLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '0.5rem 0.75rem',
    minWidth: 0,
    '@container eingabe (max-width: 16rem)': { flexBasis: '100%' },
  },
  // Mit offenem Anlassfeld nimmt die linke Gruppe schmal die ganze Zeile (DESIGN-P2 #583).
  eingabeLinksVoll: {
    '@container eingabe (max-width: 24rem)': { flexBasis: '100%' },
  },
  eingabeRechts: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem 0.75rem',
    marginLeft: 'auto',
  },
  // Die Auswahl der Wiedervorlage ist kurz: so bleibt die Fussleiste der Eingabe
  // auch in der halben Breite einzeilig.
  wiedervorlageWahl: {
    minWidth: '9rem',
    width: '9rem',
    maxWidth: '100%',
    // Schmal darf sie schrumpfen (DESIGN-P2 #583: bei 320 ragte sie 6 px hinaus).
    '@container eingabe (max-width: 24rem)': { width: '100%', minWidth: 0, maxWidth: '9rem' },
    '@container eingabe (max-width: 16rem)': { maxWidth: '100%' },
  },
  // Das Tastenkuerzel in der Fussleiste: nur, wo der Behaelter breit genug ist.
  kuerzelHinweis: {
    '@container eingabe (max-width: 28rem)': { display: 'none' },
  },
  filterLeiste: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginTop: '0.75rem',
  },
  // Das Etikett "Wiedervorlage" behaelt seine natuerliche Breite; die Auswahl
  // schrumpft (sonst ueberdeckt sie das Etikett).
  wiedervorlageFeld: {
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    '& label': { whiteSpace: 'nowrap', flexShrink: 0 },
    // Schmal schrumpft die Auswahl, statt ueber den Kasten zu ragen; die Zeile bleibt
    // einzeilig, damit "Festhalten" bei 1024 x 768 im ersten Bildschirm bleibt.
    '@container eingabe (max-width: 24rem)': { flex: '1 1 auto', minWidth: 0, maxWidth: '100%' },
    // Sehr schmal (320 px bei 200 % Textzoom) passt auch diese Zeile nicht mehr: dann steht
    // die Beschriftung ueber der Auswahl, die Auswahl volle Breite (Dirigent, P2 nach #596).
    '@container eingabe (max-width: 16rem)': { flexBasis: '100%', gridTemplateColumns: 'minmax(0, 1fr)', '& label': { whiteSpace: 'normal' } },
  },
  zeitleiste: {
    listStyle: 'none',
    margin: '0.5rem 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // v4 tl: 32-px-Punkt, 10 px Abstand zum Text, 10 px oben und unten.
  // Im Ueberblick hoechstens drei Zeilen je Notiz (DESIGN-P3 #596): so bleibt die
  // haftende Spalte auch bei langen Notizen begrenzt. Nur optisch; vorgelesen und
  // im Register "Verlauf" steht der volle Text.
  textKurz: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  zeitEintrag: {
    position: 'relative',
    paddingLeft: '2.625rem',
    paddingTop: '0.625rem',
    paddingBottom: '0.625rem',
    // Fokussierbar nur per Programm (nach "Ganzen Verlauf anzeigen"); der Ring zeigt, wo es weitergeht.
    ':focus': { outline: 'none' },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px', borderRadius: tokens.borderRadiusMedium },
  },
  // Die senkrechte Linie verbindet die Punkte; die letzte endet am Punkt.
  zeitLinie: {
    '::before': {
      content: '""',
      position: 'absolute',
      left: '15px',
      top: '2.5rem',
      bottom: '-0.375rem',
      width: '2px',
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  // Der Punkt zeigt die Art in Farbe UND Symbol (WCAG 1.4.1): Notiz in der
  // Markenflaeche, offene Wiedervorlage auf Warnflaeche, erledigt auf Erfolgsflaeche (v4 dot).
  punkt: {
    position: 'absolute',
    left: 0,
    top: '0.625rem',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  punktOffen: {
    backgroundColor: tokens.colorStatusWarningBackground1,
    color: tokens.colorStatusWarningForeground1,
  },
  punktErledigt: {
    backgroundColor: tokens.colorStatusSuccessBackground1,
    color: tokens.colorStatusSuccessForeground1,
  },
  // Art (fett), Autor, Zeit und Herkunft in einer Zeile, wo die Breite reicht (v4 U10).
  zeitKopf: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    rowGap: '0.125rem',
    columnGap: '0.625rem',
    // Die erste Zeile steht mittig neben dem 32-px-Punkt (20 px Zeile + 2 x 6 px).
    paddingTop: '0.375rem',
    color: tokens.colorNeutralForeground3,
  },
  // Autor, Zeit und Herkunft klein und still neben der Art (v4 when).
  wann: {
    color: tokens.colorNeutralForeground3,
  },
  art: {
    color: tokens.colorNeutralForeground1,
  },
  herkunft: {
    color: tokens.colorNeutralForeground3,
  },
  // Die Aktionen am Verlaufseintrag (v4 btn small): 28 px hoch, das Menue "…"
  // quadratisch; beide ueber dem Mindestziel von 24 px (WCAG 2.5.8).
  eintragKnopf: {
    minHeight: '1.75rem',
  },
  eintragMenue: {
    minWidth: '1.75rem',
    minHeight: '1.75rem',
  },
  // Ein langer Titel ohne Leerzeichen bricht um, statt bei 320 px ueberzulaufen (WCAG 1.4.10).
  faelligText: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  // "Worum geht es?" im Notizkasten: nimmt die freie Breite, schmal eine eigene Zeile.
  // Breit mit der Beschriftung links in derselben Zeile (DESIGN-P3 #569); unter
  // 24 rem Kastenbreite steht sie ueber dem Feld, das Feld volle Breite (DESIGN-P2 #583).
  anlassFeld: {
    flex: '1 1 16rem',
    minWidth: 0,
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    '& label': { whiteSpace: 'nowrap', flexShrink: 0 },
    '@container eingabe (max-width: 24rem)': { flexBasis: '100%', gridTemplateColumns: 'minmax(0, 1fr)', '& label': { whiteSpace: 'normal' } },
  },
  faelligZeile: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.25rem 0.5rem',
    marginTop: '0.25rem',
  },
  ueberfaellig: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },
});
